/**
 * Public categories API module. SF-SEARCH-002.
 *
 * Wraps the categories endpoints added by PL-CAT-003. Categories are a
 * first-class domain backed by the `categories` table, distinct from
 * taxonomy nodes (`/taxonomies/{slug}/path/{path}`).
 */
import { apiClient } from '@dfq/shared/api/client';
import { throwAsApiError, unwrap } from '@dfq/shared/api/_envelope';
import type { ApiEnvelope } from '@dfq/shared/types/auth';
import type { PublicCategoryDTO } from '@dfq/shared/types/taxonomy';

// ---------------------------------------------------------------------------
// Search / navigation types (SF-SEARCH-002)
// ---------------------------------------------------------------------------

/**
 * Recursive category node from `GET /api/v1/categories`.
 * Wire fields are snake_case matching the platform DTO shape.
 */
export interface CategoryTree {
  id: number;
  name: string;
  slug: string;
  description: string | null;
  /** Raw stored S3 key — admin-only concern (saved back on update). */
  imagePath: string | null;
  /** Resolved CDN URL, or `null` when no image is set. PL-MEDIA-101. */
  imageUrl: string | null;
  sortOrder: number;
  isVisible: boolean;
  parentId: number | null;
  /** Lucide icon name (e.g. `"car"`), or `null` when unset. SF-HOME-102. */
  icon: string | null;
  children: CategoryTree[];
}

/**
 * Fetch a single visible category by its slug path.
 *
 * Accepts a single slug (e.g. `"vehicle-wraps"`) or a slash-separated chain
 * (e.g. `"vehicle-wraps/cars"`). Returns 404 when any segment in the path
 * does not exist, is soft-deleted, or has `is_visible=false`.
 */
export async function getCategory(slugPath: string): Promise<PublicCategoryDTO> {
  try {
    const response = await apiClient.get<ApiEnvelope<{ category: PublicCategoryDTO }>>(
      `/categories/${slugPath}`,
    );
    return unwrap(response.data, response.status).category;
  } catch (err) {
    throwAsApiError(err);
  }
}

/**
 * GET /api/v1/categories — full nested category tree. SF-SEARCH-002.
 *
 * Returns `[]` when the platform returns an empty data array. HTTP errors
 * propagate as `ApiError` via `throwAsApiError`.
 */
export async function getCategories(): Promise<CategoryTree[]> {
  try {
    const response = await apiClient.get<ApiEnvelope<{ categories?: CategoryTree[] }>>('/categories');
    return unwrap(response.data, response.status).categories ?? [];
  } catch (err) {
    throwAsApiError(err);
  }
}

export const categoriesApi = {
  getCategory,
  getCategories,
};
